import { useState, useEffect, useMemo } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { fetchItems } from "../api/itemApi";
import { Item, ItemSelectRequest } from "../types/item";

/**
 * Custom hook to fetch and manage a list of items with infinite scrolling.
 * @param params - The parameters for selecting items.
 * @param page - The current page number.
 * @param setPage - Function to update the current page number.
 * @returns An object containing the list of items and a boolean indicating if there are more items to load.
 */
const useItemList = (
  params: Omit<ItemSelectRequest, "limit" | "offset">,
  page: number,
  setPage: React.Dispatch<React.SetStateAction<number>>,
) => {
  const [items, setItems] = useState<Item[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchData = async (loadMore = false) => {
    setLoading(true);
    try {
      const prevScrollY = document.documentElement.scrollTop || document.body.scrollTop;

      const { status_flag, ...restParams } = params;
      const data = await fetchItems({
        ...restParams,
        limit: DEFAULT_LIMIT_ITEMS,
        offset: page,
      });

      if (data) {
        setItems((prevItem) => (loadMore ? [...prevItem, ...data.list] : [...data.list]));
        setCount(data.total);
        if (data?.list.length < DEFAULT_LIMIT_ITEMS) {
          setHasMore(false);
        }
      }

      window.scrollTo(0, prevScrollY || 0);
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page > 1);
  }, [page]);

  const totalPage = useMemo(() => Math.ceil(count / DEFAULT_LIMIT_ITEMS), [count]);

  const onLoadMore = async () => {
    if (page + 1 > totalPage) return;
    setPage((prevPage) => prevPage + 1);
  };

  return { items, count, fetchData, hasMore, setItems, loading, onLoadMore };
};

export default useItemList;
